import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { VideoService, VideoInfoListPaginatedDTO, VideoInfoDTO, VideoSearchQuery } from '@solverml/api';
import { lastValueFrom } from 'rxjs';
import { ThumbnailUrl, VideoSearchResult, VideoSearchResultListPaginated } from '../model/video-search-result.model';
import { VideoSource } from '../model/video-source';
import { SearchQuery } from '../model/search-query';

export interface VideoBasicInfoResponse {
  video_id: string;
  title: string;
  season_id: number | null;
  match_date: string | null;
  gender?: string | null;
  competition_id?: number | null;
  category_id?: number | null;
}

export interface VideoBasicInfoUpdatePayload {
  title: string;
  season_id: number | null;
  match_date: string | null;
  gender?: string | null;
  competition_id?: number | null;
  category_id?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class VideoStreamingService {

  private readonly backendBaseUrl = `${window.location.protocol}//backend.${window.location.hostname}`;

  constructor(
    private videoService: VideoService,
    private http: HttpClient
  ) { }

  public async searchVideos(
    searchQuery: VideoSearchQuery = {}
  ): Promise<VideoSearchResultListPaginated> {

    const output: VideoSearchResultListPaginated = {
      videos: [],
      has_next_page: false,
      has_previous_page: false,
      current_page: 0,
      num_pages: 0,
      total_items: 0
    };

    const api_result: VideoInfoListPaginatedDTO = await lastValueFrom(
      this.videoService.search(searchQuery)
    );

    output.current_page = api_result.current_page;
    output.num_pages = api_result.num_pages;
    output.has_next_page = api_result.has_next_page;
    output.has_previous_page = api_result.has_previous_page;
    output.total_items = api_result.total_items;

    api_result.videos.forEach(
      (video: VideoInfoDTO) => { output.videos.push(this.parseVideoInfo(video)) }
    );
    console.log(output)
    return output;
  }

  public async getThumbnail(
    video_id: string,
    large: boolean | null = null,
    offset: number | null = null,
  ): Promise<string> {

    const response = await lastValueFrom(
      this.videoService.getThumbnail(
        video_id,
        large,
        offset,
        'response',
        false,
        { httpHeaderAccept: 'image/jpeg' } as any
      )
    );

    const url = URL.createObjectURL(response.body);

    return url;
  }

  public async getVideoById(video_id: string): Promise<VideoSearchResult | null> {
    const video = await lastValueFrom(this.videoService.getInfo(video_id));
    if (!video) {
      return null;
    }

    return this.parseVideoInfo(video);
  }

  public async getVideoBasicInfo(videoId: string): Promise<VideoBasicInfoResponse> {
    return lastValueFrom(
      this.http.get<VideoBasicInfoResponse>(
        `${this.backendBaseUrl}/video/streaming/video/${videoId}/basic-info`
      )
    );
  }

  public async updateVideoBasicInfo(
    videoId: string,
    payload: VideoBasicInfoUpdatePayload
  ): Promise<VideoBasicInfoResponse> {
    return lastValueFrom(
      this.http.put<VideoBasicInfoResponse>(
        `${this.backendBaseUrl}/video/streaming/video/${videoId}/basic-info`,
        payload
      )
    );
  }

  private parseVideoInfo(video: VideoInfoDTO): VideoSearchResult {

    let video_sources: VideoSource[] = [];

    if (video.media_urls) {
      for (const key in video.media_urls) {

        if (video.media_urls.hasOwnProperty(key)) {
          const label = key.toLowerCase();
          const url = this.parseUrlToHttpsIfapplicable(video.media_urls[key]);

          video_sources.push({
            label: label,
            url: url
          });
        }
      }
    }

    // Sort the video sources by label
    video_sources = video_sources.sort((a, b) => {

      const a_label: string = a.label.toLowerCase();
      const b_label: string = b.label.toLowerCase();

      let output = a_label.localeCompare(b_label);

      const a_label_numeric: number = Number(a_label.match(/(\d+)/)[0] || 0);
      const b_label_numeric: number = Number(b_label.match(/(\d+)/)[0] || 0);

      if (a_label_numeric !== b_label_numeric) {
        output = a_label_numeric - b_label_numeric;
      }

      return -output;
    });

    const thumbnail_url: ThumbnailUrl = {
      small: this.parseUrlToHttpsIfapplicable(video.thumbnail_urls.small),
      large: this.parseUrlToHttpsIfapplicable(video.thumbnail_urls.large)
    }

    return {
      id: video.video_id,
      title: video.title,
      description: video.description,
      date: video.date,
      match_date: (video as any).match_date ?? null,
      season_id: (video as any).season_id ?? null,
      gender: this.stringToTitleCase(video.gender || "NOT SPECIFIED"),
      category: video.category_name,
      competition: video.competition_name,
      duration_in_seconds: video.duration_in_seconds,
      media_urls: video_sources,
      thumbnail_url: thumbnail_url,
      media_type: "video/webm",
      team_check_completed: video.team_check_completed ?? false
    };
  }

  /**
   * Parses the URL to HTTPS if the current connection protocol is HTTPS.
   *
   * @param url The URL to parse
   * @returns The parsed URL
   */
  private parseUrlToHttpsIfapplicable(url: string): string {
    const current_protocol = window.location.protocol;
    let parsed_url = url;

    if (current_protocol.toLowerCase().includes('https')) {
      // If the current protocol is HTTPS, parse the URL to HTTPS
      parsed_url = parsed_url.replace("http://", "https://");
    }

    return parsed_url;
  }

  private stringToTitleCase(str: string): string {
    return str.toLowerCase().replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }

}
